export const RESEARCH_PATHS = ['stewardship', 'warcraft', 'statecraft'];
const cost = (wood, clay, iron, grain) => ({
    wood,
    clay,
    iron,
    grain,
});
/**
 * The tree.
 *
 * Read down a column, not across. Stewardship covers production and building,
 * warcraft covers training, statecraft covers the domain limit and claims.
 * Nothing in one column helps with another, which is what makes the choice a
 * choice.
 */
export const RESEARCH = [
    // --- Stewardship: the land pays better -------------------------------------
    {
        id: 'three_field',
        path: 'stewardship',
        name: 'Three-Field Rotation',
        blurb: 'Every holding produces 6% more of every resource.',
        rung: 1,
        hours: 6,
        manor: 5,
        cost: cost(900, 900, 400, 300),
        effect: { production: 0.06 },
    },
    {
        id: 'master_masons',
        path: 'stewardship',
        name: 'Master Masons',
        blurb: 'Buildings complete 10% faster in every holding.',
        rung: 2,
        hours: 18,
        manor: 10,
        cost: cost(2_600, 2_600, 1_400, 800),
        effect: { buildSpeed: 0.1 },
    },
    {
        id: 'deep_mines',
        path: 'stewardship',
        name: 'Deep Mines',
        blurb: 'Every holding produces a further 7% more of every resource.',
        rung: 3,
        hours: 40,
        manor: 15,
        cost: cost(7_000, 7_000, 4_500, 2_200),
        effect: { production: 0.07 },
    },
    {
        id: 'crown_works',
        path: 'stewardship',
        name: 'Crown Works',
        blurb: 'Buildings complete a further 12% faster.',
        rung: 4,
        hours: 72,
        manor: 20,
        cost: cost(18_000, 18_000, 12_000, 6_000),
        effect: { buildSpeed: 0.12 },
    },
    // --- Warcraft: the levy musters sooner --------------------------------------
    {
        id: 'drill_yards',
        path: 'warcraft',
        name: 'Drill Yards',
        blurb: 'Troops train 10% faster in every holding.',
        rung: 1,
        hours: 6,
        manor: 5,
        cost: cost(700, 500, 900, 500),
        effect: { trainSpeed: 0.1 },
    },
    {
        id: 'muster_rolls',
        path: 'warcraft',
        name: 'Muster Rolls',
        blurb: 'Troops train a further 10% faster.',
        rung: 2,
        hours: 18,
        manor: 10,
        cost: cost(2_000, 1_600, 3_000, 1_400),
        effect: { trainSpeed: 0.1 },
    },
    {
        id: 'armouries',
        path: 'warcraft',
        name: 'Standing Armouries',
        blurb: 'Buildings complete a further 8% faster.',
        rung: 3,
        hours: 40,
        manor: 15,
        cost: cost(5_500, 4_500, 8_000, 3_500),
        effect: { buildSpeed: 0.08 },
    },
    {
        id: 'veteran_cadres',
        path: 'warcraft',
        name: 'Veteran Cadres',
        blurb: 'Troops train a further 13% faster.',
        rung: 4,
        hours: 72,
        manor: 20,
        cost: cost(14_000, 12_000, 20_000, 9_000),
        effect: { trainSpeed: 0.13 },
    },
    // --- Statecraft: the realm reaches further ----------------------------------
    {
        id: 'chancery',
        path: 'statecraft',
        name: 'The Chancery',
        blurb: 'Your domain limit rises by 1.',
        rung: 1,
        hours: 6,
        manor: 5,
        cost: cost(800, 800, 500, 700),
        effect: { domainLimit: 1 },
    },
    {
        id: 'heralds',
        path: 'statecraft',
        name: 'College of Heralds',
        blurb: 'Claims cost 15% less prestige.',
        rung: 2,
        hours: 18,
        manor: 10,
        cost: cost(2_200, 2_200, 1_600, 2_000),
        effect: { claimCost: 0.15 },
    },
    {
        id: 'vassal_courts',
        path: 'statecraft',
        name: 'Vassal Courts',
        blurb: 'Your domain limit rises by 1.',
        rung: 3,
        hours: 40,
        manor: 15,
        cost: cost(6_500, 6_500, 5_000, 6_000),
        effect: { domainLimit: 1 },
    },
    {
        id: 'imperial_chancery',
        path: 'statecraft',
        name: 'Imperial Chancery',
        blurb: 'Your domain limit rises by 1, and claims cost a further 20% less prestige.',
        rung: 4,
        hours: 72,
        manor: 20,
        cost: cost(16_000, 16_000, 14_000, 15_000),
        effect: { domainLimit: 1, claimCost: 0.2 },
    },
];
export const RESEARCH_BY_ID = Object.fromEntries(RESEARCH.map((n) => [n.id, n]));
/** The node directly above this one on its road, or null at the top. */
export function researchParent(node) {
    return RESEARCH.find((n) => n.path === node.path && n.rung === node.rung - 1) ?? null;
}
/**
 * Everything a lord's completed research adds up to.
 *
 * Percentages *stack additively* rather than compounding. Two 10% reductions
 * are 20%, not 19%: compounding is technically neater and reads as a bug to
 * every player who does the arithmetic, and the numbers here are small enough
 * that the difference never matters.
 *
 * Speed reductions are clamped well short of free. A road that eventually made
 * building instant would delete the game's whole sense of time, and the clamp
 * is the guard against a later node being added without anyone rechecking the
 * total.
 */
export function researchBonuses(unlocked) {
    const out = {
        production: 0,
        buildSpeed: 0,
        trainSpeed: 0,
        domainLimit: 0,
        claimCost: 0,
    };
    for (const id of unlocked ?? []) {
        const node = RESEARCH_BY_ID[id];
        if (!node)
            continue;
        out.production += node.effect.production ?? 0;
        out.buildSpeed += node.effect.buildSpeed ?? 0;
        out.trainSpeed += node.effect.trainSpeed ?? 0;
        out.domainLimit += node.effect.domainLimit ?? 0;
        out.claimCost += node.effect.claimCost ?? 0;
    }
    out.buildSpeed = Math.min(0.5, out.buildSpeed);
    out.trainSpeed = Math.min(0.5, out.trainSpeed);
    out.claimCost = Math.min(0.5, out.claimCost);
    return out;
}
//# sourceMappingURL=research.js.map